<template>
  <Layout>
    <div data-component="ActionsList" :class="`w-full px-4 ${loading ? 'loading' : ''}`">
    <div v-if="!errored">
      <div class="list-header">
        <h3 :class="{ 'animate-blinking': totalLoading }">{{ mapTotalToTitle(total)(totalLoading) }}</h3>
        <ButtonFilter 
          :stringifiedFilters="stringifiedFilters" 
          :filtersNumber="filtersNumber" 
          :blink="loading"
          subject="ActionsList"
        />
      </div>
      <div class="list-wrapper">
        <ListHead 
          :columns="columns"
          :onSort="onSort"
          :loading="loading"
        />
        <ListBody 
          :rows="rows"
          :onAttribute="onAttribute"
          :onPlanify="onPlanify"
          :onResetPlanification="onResetPlanification"
          :onCheck="onCheck"
          :onDelete="onDelete"
          :onPostpone="onPostpone"
          :toggleDropdown="toggleDropdown"
          :closeDropdown="closeDropdown"
        />
      </div>
    </div>

    <WifiError v-if="errored" />
    
    <div class="bottom-overlay"></div>
    </div>
  </Layout>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import ButtonFilter from '../components/List/ButtonFilter.vue'
import ListHead from '../components/List/Head.vue'
import ListBody from '../components/List/Body.vue'
import WifiError from '../components/Common/WifiError.vue'
import AppLayout from '../components/Layout/AppLayout.vue'
import { buildUrlWithPreservedParams } from '../utils/urlHelper'
import { getFiltersWithDefaults } from '../services/filterStorageService'
import {
  formatDate,
  isVisitableProperty,
  getVisitStatusValue,
  getScheduledVisitColumn,
  getAttributionVisiteColumn,
  getDelegationText
} from '../utils/lot'

const SORT_NONE = 'None'
const SORT_ASC = 'Asc'
const SORT_DESC = 'Desc'

export default {
  name: 'ActionsListView',
  components: {
    ButtonFilter,
    ListHead,
    ListBody,
    WifiError,
    Layout: AppLayout
  },
  props: {
    mapTotalToTitle: {
      type: Function,
      default: (total) => (loading) => loading ? 'Chargement...' : `${total} actions`
    }
  },
  data() {
    return {
      loading: false,
      totalLoading: false,
      errored: false,
      total: 0,
      activeFilters: {},
      stringifiedFilters: '',
      filtersNumber: 0,
      columns: [
        { label: 'Identifiant NSIL', sortId: 'nsil', sortable: true, direction: SORT_NONE, sortDexieId: ['nsil+id'] },
        { label: 'Code postal', sortId: 'zipCode', sortable: true, direction: SORT_NONE, sortDexieId: ['address.zipCode+id'] },
        { label: 'Type', sortId: 'propertyType', sortable: true, direction: SORT_NONE, sortDexieId: ['type+id'] },
        { label: 'Mandant', sortId: 'assistanceMandator', sortable: true, direction: SORT_NONE, sortDexieId: ['assistance.mandator.id+id'] },
        { label: 'Attribution visite', sortId: 'technicalManager', sortable: true, direction: SORT_NONE, sortDexieId: ['scheduledVisitStatusSortAsc+technicalManager.fullName+id'] },
        { label: 'A visiter avant', sortId: 'scheduledVisitAt', sortable: true, direction: SORT_ASC, sortDexieId: ['scheduledVisitAtSortAsc+scheduledVisitAt+scheduledVisitStatusSortAsc+scheduledVisitStatus+id'] },
        { label: 'Nb actions', sortId: 'nbNeededActions', sortable: true, direction: SORT_NONE, sortDexieId: ['nbNeededActions+id'] }
      ],
      rows: [],
      pagination: {
        page: 1,
        limit: 12,
        total: 0
      },
      progressiveLoadingEnabled: true,
      loadingBatchSize: 5,
      loadingDelay: 100,
      isLoadingMore: false,
      hasMoreData: true,
      scrollThreshold: 250,
      propertyTypes: {},
      visitStatuses: [],
      assistanceMandators: {},
      managerMandators: {}
    }
  },
  computed: {
    ...mapState('list', ['filters']),
    ...mapGetters('attribution', ['lastAttributionUpdate'])
  },
  async mounted() {
    // Charger les filtres depuis localStorage (avec defaults si première visite)
    this.activeFilters = getFiltersWithDefaults('ActionsList')
    await this.loadPropertyTypes()
    await this.loadVisitStatuses()
    await this.loadAssistanceMandators()
    await this.loadManagerMandators()
    await this.loadItems()
    this.setupScrollListener()
  },
  beforeUnmount() {
    this.removeScrollListener()
  },
  watch: {
    // Rechargement lors du retour depuis la page de filtres
    '$route'(to) {
      if (to.path === '/actions') {
        this.activeFilters = getFiltersWithDefaults('ActionsList')
        this.loadItems()
      }
    },
    lastAttributionUpdate(newVal) {
      if (newVal) {
        // Recharger les données quand une attribution a été modifiée
        this.loadItems()
      }
    }
  },
  methods: {
    ...mapActions('list', [
      'setItems', 
      'setLoading', 
      'setError', 
      'setPagination',
      'setTotalLoading',
      'setProgressiveLoading',
      'setReady',
      'updateRowLoading'
    ]),

    async loadPropertyTypes() {
      try {
        const dexieService = (await import('../services/dexieService')).default
        let cachedTypes = await dexieService.getDictionary('property_type')
        
        // Vérifier si les données sont dans la structure de l'ancienne app (avec items)
        if (cachedTypes && cachedTypes.items && Array.isArray(cachedTypes.items)) {
          // Convertir le tableau items en objet clé-valeur
          const typeMap = {}
          cachedTypes.items.forEach(item => {
            if (item.key && item.value) {
              typeMap[item.key] = item.value
            }
          })
          this.propertyTypes = typeMap
          return
        }
        
        // Sinon, vérifier si c'est déjà un objet clé-valeur
        if (cachedTypes && Object.keys(cachedTypes).length > 0) {
          this.propertyTypes = cachedTypes
          return
        }
        
        // Ne pas faire d'appel API - utiliser uniquement les données locales
        this.propertyTypes = {}
      } catch (error) {
        this.propertyTypes = {}
      }
    },

    async loadVisitStatuses() {
      try {
        const dexieService = (await import('../services/dexieService')).default
        let cachedStatuses = await dexieService.getDictionary('visit_status')
        
        // Vérifier si les données sont dans la structure de l'ancienne app (avec items)
        if (cachedStatuses && cachedStatuses.items && Array.isArray(cachedStatuses.items)) {
          // Garder le format tableau { key, value } comme dans l'ancienne app React
          this.visitStatuses = cachedStatuses.items
          return
        }
        
        // Sinon, vérifier si c'est déjà un objet clé-valeur et le convertir en tableau
        if (cachedStatuses && Object.keys(cachedStatuses).length > 0) {
          this.visitStatuses = Object.entries(cachedStatuses).map(([key, value]) => ({ key, value }))
          return
        }
        
        // Si pas de cache
        this.visitStatuses = []
      } catch (error) {
        this.visitStatuses = []
      }
    },

    async loadAssistanceMandators() {
      try {
        const { assistanceMandators } = await import('../services/dexieService')
        
        // Récupérer tous les assistanceMandators depuis Dexie
        const mandators = await assistanceMandators().toArray()
        
        // Créer une map pour un accès rapide par ID
        const mandatorsMap = {}
        mandators.forEach(mandator => {
          if (mandator.id && mandator.name) {
            mandatorsMap[mandator.id] = mandator.name
          }
        })
        
        this.assistanceMandators = mandatorsMap
      } catch (error) {
        console.error('Erreur lors du chargement des assistanceMandators:', error)
        this.assistanceMandators = {}
      }
    },

    async loadManagerMandators() {
      try {
        const { managerMandators } = await import('../services/dexieService')
        
        // Récupérer tous les managerMandators depuis Dexie
        const mandators = await managerMandators().toArray()
        
        // Créer une map pour un accès rapide par ID avec nom et division
        const mandatorsMap = {}
        mandators.forEach(mandator => {
          if (mandator.id) {
            mandatorsMap[mandator.id] = {
              name: mandator.name || '',
              division: mandator.division || ''
            }
          }
        })
        
        this.managerMandators = mandatorsMap
      } catch (error) {
        console.error('Erreur lors du chargement des managerMandators:', error)
        this.managerMandators = {}
      }
    },

    async loadItems(isLoadMore = false) {
      if (!isLoadMore) {
        this.resetPagination()
        this.loading = true
        this.totalLoading = true
        this.errored = false
      } else {
        if (this.isLoadingMore) return
        this.isLoadingMore = true
      }
      
      try {
        const dexieService = (await import('../services/dexieService')).default
        
        if (!isLoadMore) {
          this.stringifiedFilters = new URLSearchParams(this.activeFilters).toString()
          this.filtersNumber = Object.keys(this.activeFilters).length
        }
        
        let items = []
        let total = 0
        
        if (isLoadMore) {
          // Pour le chargement de plus d'éléments, ajouter des placeholders
          const loadingPlaceholders = this.createLoadingPlaceholders(this.pagination.limit)
          this.rows = [...this.rows, ...loadingPlaceholders]
        }
        
        // Calculer la page actuelle
        const currentPage = isLoadMore ? Math.floor(this.rows.filter(row => !row.loading).length / this.pagination.limit) + 1 : 1
        
        // Récupérer les propriétés paginées avec le filtre nbNeededActions > 0 ET visitables (exclut les lots "occupé" etc.)
        const allProperties = await dexieService.getAllProperties()
        let propertiesWithActions = allProperties.filter(property => 
          property.nbNeededActions && property.nbNeededActions > 0 &&
          property.scheduledVisitAt && property.scheduledVisitAt !== null &&
          isVisitableProperty(property.scheduledVisitStatus)
        )
        
        // Appliquer le tri sur toutes les données
        const activeColumn = this.columns.find(col => col.direction !== SORT_NONE)
        if (activeColumn) {
          propertiesWithActions = this.sortAllProperties(propertiesWithActions, activeColumn.sortId, activeColumn.direction)
        }
        
        total = propertiesWithActions.length
        
        // Appliquer la pagination manuelle
        const startIndex = (currentPage - 1) * this.pagination.limit
        const endIndex = startIndex + this.pagination.limit
        items = propertiesWithActions.slice(startIndex, endIndex)
        
        this.hasMoreData = endIndex < total
        
        if (!isLoadMore) {
          this.total = total
        }
        
        if (isLoadMore) {
          // Nettoyer les placeholders de chargement
          this.rows = this.rows.filter(row => !row.loading)
        }
        
        if (items.length > 0) {
          if (this.progressiveLoadingEnabled) {
            await this.loadItemsProgressively(items, isLoadMore)
          } else {
            const newRows = this.transformItemsToRows(items)
            if (isLoadMore) {
              this.rows = [...this.rows, ...newRows]
            } else {
              this.rows = newRows
            }
          }
        }
        
        if (!isLoadMore) {
          this.pagination = {
            page: 1,
            limit: this.pagination.limit || 12,
            total: total
          }
          
          this.setItems(propertiesWithActions)
          this.setPagination(this.pagination)
        } else {
          this.hasMoreData = items.length === this.pagination.limit && this.rows.length < this.total
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
        if (!isLoadMore) {
          this.errored = true
          this.setError(error.message)
        } else {
          this.rows = this.rows.filter(row => !row.loading)
        }
      } finally {
        if (!isLoadMore) {
          this.loading = false
          this.totalLoading = false
          this.setLoading(false)
        } else {
          this.isLoadingMore = false
        }
      }
    },

    async loadItemsProgressively(items, isLoadMore = false) {
      if (isLoadMore) {
        // Pour le chargement de plus d'éléments, ajouter des placeholders à la fin
        const placeholders = this.createLoadingPlaceholders(items.length)
        this.rows = [...this.rows, ...placeholders]
      } else {
        // Pour le chargement initial, remplacer tout par des placeholders
        this.rows = this.createLoadingPlaceholders(items.length)
      }
      
      for (let i = 0; i < items.length; i += this.loadingBatchSize) {
        const batch = items.slice(i, i + this.loadingBatchSize)
        const batchRows = this.transformItemsToRows(batch)
        
        batchRows.forEach((row, index) => {
          let globalIndex
          if (isLoadMore) {
            // Pour le chargement de plus d'éléments, calculer l'index à partir de la fin
            globalIndex = this.rows.length - items.length + i + index
          } else {
            // Pour le chargement initial
            globalIndex = i + index
          }
          
          if (globalIndex < this.rows.length && globalIndex >= 0) {
            this.rows[globalIndex] = row
          }
        })
        
        if (i + this.loadingBatchSize < items.length) {
          await this.delay(this.loadingDelay)
        }
      }
    },

    createLoadingPlaceholders(count) {
      const placeholders = []
      for (let i = 0; i < count; i++) {
        placeholders.push({
          id: `loading-${i}`,
          checked: false,
          loading: true,
          data: this.createLoadingRowData(),
          dropdown: {
            open: false,
            actions: []
          }
        })
      }
      return placeholders
    },

    createLoadingRowData() {
      const columnCount = this.columns.length
      const loadingData = []
      
      for (let i = 0; i < columnCount; i++) {
        loadingData.push([
          { type: 'TextLoading', value: this.getLoadingPlaceholderClass(i) }
        ])
      }
      
      return loadingData
    },

    getLoadingPlaceholderClass(columnIndex) {
      const classes = ['short', 'medium', 'long', 'short']
      return classes[columnIndex % classes.length] || 'medium'
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    transformItemsToRows(items) {
      return items.map(item => ({
        id: item.id,
        checked: false,
        loading: false,
        data: this.getRowData(item),
        dropdown: {
          open: false,
          actions: []
        }
      }))
    },

    getRowData(item) {
      return [
        // Colonne 1: Identifiant NSIL
        [
          { type: 'TextBoldBlue', value: item.nsil || item.propertyCode || `Lot ${item.id}`, link: buildUrlWithPreservedParams(`/dossier/${item.id}/timeline`, this.$route) },
          { type: 'Text', value: `Immeuble - ${item.propertyCode || ''}` },
          { type: 'Text', value: `Lot - ${item.unitCode || ''}` }
        ],
        // Colonne 2: Code postal
        [
          { type: 'TextBold', value: this.getZipCode(item) },
          { type: 'Text', value: this.getStreet(item) },
          { type: 'TextBold', value: this.getCity(item) }
        ],
        // Colonne 3: Type
        [
          { type: 'TextBold', value: this.getPropertyType(item) },
          { type: 'Text', value: item.occupancy || '' }
        ],
        // Colonne 4: Mandant
        [
          { type: 'TextBold', value: this.getAssistanceMandator(item) },
          { type: 'Text', value: this.getManagerMandator(item) },
          { type: 'Text', value: this.getLegalOwnerWithDivision(item) }
        ],
        // Colonne 5: Attribution visite
        this.getAttributionVisiteColumn(item),
        // Colonne 6: A visiter avant
        this.getScheduledVisitColumn(item),
        // Colonne 7: Nb actions
        [
          {
            type: 'TextBoldBlue',
            value: `${item.nbNeededActions || 0} action(s)`,
            link: buildUrlWithPreservedParams(`/dossier/${item.id}/actions-et-travaux`, this.$route)
          }
        ]
      ]
    },

    // Méthodes utilitaires pour extraire les données
    getZipCode(item) {
      return item.address?.zipCode || ''
    },

    getStreet(item) {
      return item.address?.street || ''
    },

    getCity(item) {
      return item.address?.city || ''
    },

    getPropertyType(item) {
      const typeCode = item.type || ''
      
      if (this.propertyTypes && this.propertyTypes[typeCode]) {
        return this.propertyTypes[typeCode]
      }
      
      return typeCode || 'Non défini'
    },

    getAssistanceMandator(item) {
      const mandatorId = item.assistance?.mandator?.id
      if (!mandatorId) return item.assistance?.mandator?.name || '-'
      
      // Chercher le nom dans la table assistanceMandators
      const mandatorName = this.assistanceMandators[mandatorId]
      // Fallback sur le nom stocké directement dans la propriété (ex: sync depuis MSSQL)
      return mandatorName || item.assistance?.mandator?.name || '-'
    },

    getManagerMandator(item) {
      const mandatorId = item.manager?.mandator?.id
      if (!mandatorId) return null
      
      // Chercher le nom et division dans la table managerMandators
      const mandatorData = this.managerMandators[mandatorId]
      if (!mandatorData) return null
      
      const name = mandatorData.name || ''
      const division = mandatorData.division || ''
      
      // Si pas de nom, retourner null
      if (!name) return division || null
      
      // Si pas de division, retourner juste le nom
      if (!division) return name
      
      // Si le nom contient déjà la division, ne pas la répéter
      if (name.includes(division)) {
        return name
      }
      
      // Sinon retourner "Nom - Division"
      return `${name} - ${division}`
    },

    getLegalOwnerWithDivision(item) {
      const legalOwner = item.legalOwner
      if (!legalOwner) return null
      
      const name = legalOwner.name || ''
      const division = legalOwner.division || ''
      
      // Si pas de nom, retourner null
      if (!name) return division || null
      
      // Si pas de division, retourner juste le nom
      if (!division) return name
      
      // Si nom et division sont identiques, ne pas répéter
      if (name === division) {
        return name
      }
      
      // Si le nom contient déjà la division, ne pas la répéter
      if (name.includes(division)) {
        return name
      }
      
      // Sinon retourner "Nom - Division"
      return `${name} - ${division}`
    },

    getAttributionVisiteColumn(item) {
      const result = getAttributionVisiteColumn(item)
      
      return result
    },

    getScheduledVisitColumn(item) {
      const result = getScheduledVisitColumn(item, this.visitStatuses)
      
      return result
    },

    isVisitableProperty(scheduledVisitStatus) {
      return isVisitableProperty(scheduledVisitStatus)
    },

    getDelegationText(item) {
      return getDelegationText(item)
    },

    getVisitStatusValue(scheduledVisitStatus) {
      return getVisitStatusValue(scheduledVisitStatus, this.visitStatuses)
    },

    formatDate(date) {
      return formatDate(date)
    },

    sortAllProperties(properties, sortId, direction) {
      const multiplier = direction === SORT_DESC ? -1 : 1
      
      return [...properties].sort((a, b) => {
        let valueA, valueB
        
        switch (sortId) {
          case 'nsil':
            valueA = a.nsil || ''
            valueB = b.nsil || ''
            break
            
          case 'zipCode':
            valueA = a.address?.zipCode || ''
            valueB = b.address?.zipCode || ''
            break
            
          case 'propertyType':
            valueA = this.getPropertyType(a)
            valueB = this.getPropertyType(b)
            break
            
          case 'assistanceMandator':
            valueA = this.getAssistanceMandator(a)
            valueB = this.getAssistanceMandator(b)
            break
            
          case 'technicalManager':
            valueA = a.technicalManager?.fullName || ''
            valueB = b.technicalManager?.fullName || ''
            break
            
          case 'scheduledVisitAt': {
            // Utiliser les valeurs pré-calculées qui prennent en compte le statut ET la date
            if (direction === SORT_DESC) {
              valueA = a.scheduledVisitAtSortDesc !== undefined ? a.scheduledVisitAtSortDesc : 0
              valueB = b.scheduledVisitAtSortDesc !== undefined ? b.scheduledVisitAtSortDesc : 0
            } else {
              valueA = a.scheduledVisitAtSortAsc !== undefined ? a.scheduledVisitAtSortAsc : Number.MAX_SAFE_INTEGER
              valueB = b.scheduledVisitAtSortAsc !== undefined ? b.scheduledVisitAtSortAsc : Number.MAX_SAFE_INTEGER
            }
            
            // Comparer les valeurs pré-calculées
            if (valueA < valueB) return -1
            if (valueA > valueB) return 1
            
            // En cas d'égalité, trier par ID
            if (a.id < b.id) return -1
            if (a.id > b.id) return 1
            return 0
          }
            
          case 'nbNeededActions':
            valueA = a.nbNeededActions || 0
            valueB = b.nbNeededActions || 0
            return (valueA - valueB) * multiplier
            
          default:
            return 0
        }
        
        // Comparaison générique pour les valeurs string
        if (sortId !== 'scheduledVisitAt' && sortId !== 'nbNeededActions') {
          if (valueA < valueB) return -1 * multiplier
          if (valueA > valueB) return 1 * multiplier
          return 0
        }
        
        return 0
      })
    },

    onSort(direction) {
      return (sortId) => {
        // Mettre à jour l'état des colonnes
        this.columns = this.columns.map(col => ({
          ...col,
          direction: col.sortId === sortId ? direction : SORT_NONE
        }))
        
        // Recharger les données avec le nouveau tri
        this.loadItems()
      }
    },

    onAttribute() {
      return () => {
        // TODO: Implement attribute logic
      }
    },

    onPlanify() {
      return () => {
        // TODO: Implement planify logic
      }
    },

    onResetPlanification() {
      return () => {
        // TODO: Implement reset planification logic
      }
    },

    onCheck(id) {
      return () => {
        this.rows = this.rows.map(row => 
          row.id === id ? { ...row, checked: !row.checked } : row
        )
      }
    },

    onDelete() {
      return () => {
        // TODO: Implement delete logic
      }
    },

    onPostpone() {
      return () => {
        // TODO: Implement postpone logic
      }
    },

    toggleDropdown(id) {
      return () => {
        this.rows = this.rows.map(row => ({
          ...row,
          dropdown: {
            ...row.dropdown,
            open: row.id === id ? !row.dropdown.open : false
          }
        }))
      }
    },

    closeDropdown() {
      return () => {
        this.rows = this.rows.map(row => ({
          ...row,
          dropdown: {
            ...row.dropdown,
            open: false
          }
        }))
      }
    },

    // Méthodes pour la pagination par scroll infini
    setupScrollListener() {
      window.addEventListener('scroll', this.handleScroll)
    },

    removeScrollListener() {
      window.removeEventListener('scroll', this.handleScroll)
    },

    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      const distanceFromBottom = documentHeight - (scrollTop + windowHeight)

      if (distanceFromBottom < this.scrollThreshold && this.shouldLoadMore()) {
        this.loadMoreItems()
      }
    },

    shouldLoadMore() {
      return (
        !this.isLoadingMore && 
        !this.loading && 
        this.hasMoreData && 
        this.rows.length < this.total &&
        this.rows.filter(row => !row.loading).length > 0
      )
    },

    async loadMoreItems() {
      await this.loadItems(true)
    },

    async loadNewItemsProgressively(newRows) {
      const placeholders = this.createLoadingPlaceholders(newRows.length)
      this.rows = [...this.rows, ...placeholders]

      for (let i = 0; i < newRows.length; i += this.loadingBatchSize) {
        const batch = newRows.slice(i, i + this.loadingBatchSize)
        
        batch.forEach((row, index) => {
          const globalIndex = this.rows.length - newRows.length + i + index
          if (globalIndex < this.rows.length) {
            this.rows[globalIndex] = row
          }
        })
        
        if (i + this.loadingBatchSize < newRows.length) {
          await this.delay(this.loadingDelay)
        }
      }
    },

    resetPagination() {
      this.rows = []
      this.pagination.page = 1
      this.hasMoreData = true
      this.isLoadingMore = false
    }
  }
}
</script>

<style lang="scss">
@use './../assets/styles/components/List.scss';
</style>
